import type { QueryClient } from "@tanstack/react-query";
import { sessionQueryOptions } from "~/app/api/pageData/sessionQuery";
import { removeGlobalSlotsCache } from "~/app/api/pageData/globalSlotsQuery";
import { queryClient as appQueryClient } from "~/app/queryClient";
import { baseApi } from "~/app/store/baseApi";
import type { AppDispatch, AppStore } from "~/app/store";
import { accountContextSwitchSucceeded } from "~/common/accountContextTransition";
import { cancelAndClearPna } from "~/domains/pna/pnaLifecycle";
import { beginAccountContextTransition, finishAccountContextTransition } from "~/app/accountContextTransitionState";

export interface AccountContextTransitionDependencies {
  queryClient: QueryClient;
  dispatch: AppDispatch;
  establishSession?: (queryClient: QueryClient) => Promise<unknown>;
}

let registeredDependencies: AccountContextTransitionDependencies | null = null;

/**
 * Registers the production cache owners at the application composition root.
 * Tests can pass isolated dependencies to runAccountContextTransition instead.
 */
export function initializeAccountContextTransition(store: AppStore): void {
  registeredDependencies = { queryClient: appQueryClient, dispatch: store.dispatch };
}

function getRegisteredDependencies(): AccountContextTransitionDependencies {
  if (!registeredDependencies) {
    throw new Error("Account context transition is not initialized at the application composition root.");
  }
  return registeredDependencies;
}

/**
 * Prepares the authenticated Sold-To Account context boundary immediately after OCC accepts the
 * switch. Storefront session synchronization follows this fail-closed preparation; a sync failure
 * therefore cannot leave old-account endpoint data renderable.
 */
export async function prepareAccountContextTransition(
  dependencies: AccountContextTransitionDependencies = getRegisteredDependencies(),
): Promise<void> {
  const { queryClient, dispatch } = dependencies;

  // Mounted Session observers can retain their previous result even after QueryCache.clear(). Mask
  // every Session consumer synchronously until the new authoritative projection is established.
  beginAccountContextTransition();

  // Cancel queued/running TanStack-owned PnA work and remove the whole PnA namespace so an
  // old-account query cannot repopulate a cache entry after the boundary (task 0042). The
  // generic cancel/clear below still runs for every other namespace.
  await cancelAndClearPna(queryClient);

  // Cancel TanStack queries next so an old-account request cannot repopulate a cache entry after
  // the boundary.
  await queryClient.cancelQueries();

  // Clear the complete TanStack cache. Account Selection performs a full navigation after this
  // boundary, so the destination route creates/refetches the data it needs under the new context.
  // Clearing rather than merely invalidating prevents old endpoint values from rendering while a
  // background request is in flight, and also removes mutation-cache entries for the old account.
  queryClient.clear();

  // The global-slots persister outlives the in-memory cache in sessionStorage; drop its entries
  // too so a switch back to a previous Sold-To Account cannot restore that account's shell slots
  // (the cache key alone would not protect against it within the persister's maxAge window).
  removeGlobalSlotsCache();

  // RTK Query owns a separate endpoint cache and must be reset independently.
  dispatch(baseApi.util.resetApiState());

  // The root reducer owns the complete endpoint-derived Redux inventory and deliberately retains
  // only interaction-only/account-independent state.
  dispatch(accountContextSwitchSucceeded());
}

/** Re-establish the authoritative Session projection after storefront synchronization succeeds. */
export async function completeAccountContextTransition(
  dependencies: AccountContextTransitionDependencies = getRegisteredDependencies(),
): Promise<void> {
  const { queryClient, establishSession } = dependencies;
  await (establishSession
    ? establishSession(queryClient)
    : queryClient.fetchQuery(sessionQueryOptions({ allowDuringAccountTransition: true })));
  finishAccountContextTransition();
}

/** Convenience seam for tests and callers that already completed both server synchronizations. */
export async function runAccountContextTransition(
  dependencies: AccountContextTransitionDependencies = getRegisteredDependencies(),
): Promise<void> {
  await prepareAccountContextTransition(dependencies);
  await completeAccountContextTransition(dependencies);
}
